import {
  AutoEnrollmentDefaultConditionsModel,
  AutoEnrollmentDocumentsModel,
  AutoEnrollmentModel,
  AutoEnrollmentProcessStatusEnum,
  AutoEnrollmentRepository,
  AutoEnrollmentSteps,
  AutoEnrollmentTypes,
} from '@/AutoEnrollment/domain'
import { Step1Form, Step2Form, Step3Form } from '@/AutoEnrollment/ui/hooks'
import { Step2Form as OwnerDataForm } from '@/Contract/ui/hooks'
import { LogaltyModel, LogaltyResponseStatus } from '@/Logalty/domain'
import { HttpMethod, HttpModel, MetaModel, documentType } from '@/Shared/domain'
import { axiosFetch, createQueryString } from '@/Shared/infrastructure'
import {
  checkCifAlreadyExists,
  getAutoEnrollmentData,
  getAutoEnrollmentDefaultConditions,
  getAutoEnrollmentDocuments,
  getAutoEnrollmentLogaltyStatus,
  getAutoEnrollmentProductData,
  saveAutoEnrollmentFinantialData,
  saveAutoEnrollmentOwnerData,
  saveAutoEnrollmentProducts,
  saveAutoEnrollmentStoreData,
} from '@/Shared/infrastructure/endpoints'

export function createApiAutoEnrollmentRepository(): AutoEnrollmentRepository {
  return new ApiAutoEnrollmentRepository()
}

const computeAutoEnrollmentCurrentStep = (
  processStatus: AutoEnrollmentProcessStatusEnum,
): AutoEnrollmentSteps => {
  switch (processStatus) {
    case 1:
      return AutoEnrollmentSteps.BILLING_DATA
    case 2:
      return AutoEnrollmentSteps.STORE_DATA
    case 3:
      return AutoEnrollmentSteps.DOCUMENTATION
    case 4:
      return AutoEnrollmentSteps.DOCUMENTATION
    case 5:
      return AutoEnrollmentSteps.DOCUMENTATION
    case 6:
      return AutoEnrollmentSteps.CONFIRMATION
    case 7:
      return AutoEnrollmentSteps.DOCUMENTATION
    case 8:
      return AutoEnrollmentSteps.CONFIRMATION
    default:
      return AutoEnrollmentSteps.BILLING_DATA
  }
}

type ApiLogaltyResponseStatus = {
  data: {
    hasEnded: boolean
    hasEndedSuccesfully: boolean
  }
  meta: MetaModel
}

const computeLogaltyResponseStatus = ({
  data,
}: ApiLogaltyResponseStatus): LogaltyResponseStatus => {
  if (data.hasEnded) {
    return data.hasEndedSuccesfully
      ? LogaltyResponseStatus.Success
      : LogaltyResponseStatus.Error
  }

  return LogaltyResponseStatus.Pending
}

type ApiTpvsData = {
  terminalMid: string
}

type ApiAutoEnrollmentModel = {
  id: number
  guidId: string
  creationDate: string
  email: string | null
  idUser: number
  processType: AutoEnrollmentTypes
  processStatus: AutoEnrollmentProcessStatusEnum
  merchantId: number | null
  finantialData: {
    merchantBusinessName: string | null
    merchantCIF: string | null
    iban: string | null
    addressRoadTypeName: string | null
    addressStreetName: string | null
    addressStreetNumber: string | null
    addressAdditionalData1: string | null
    addressAdditionalData12: string | null
    addressZipCode: string | null
    addressTown: string | null
    addressProvince: string | null
  } | null
  billingContact: {
    contacFirstName: string | null
    contactSurname1: string | null
    contactSurname2: string | null
    contactEmailAddress: string | null
    contactPhone: string | null
  } | null
  storeData: {
    merchantName: string | null
    addressRoadTypeName: string | null
    addressStreetName: string | null
    addressStreetNumber: string | null
    addressAdditionalData1: string | null
    addressAdditionalData12: string | null
    addressZipCode: string | null
    addressTown: string | null
    addressProvince: string | null
    addressTelephone: string | null
    addressEmail: string | null
    merchantWebUrl: string | null
    merchantCoockTypeId: number | null
    merchantCoockType: string | null
    merchantIsOnlineStore: boolean
    merchantHasHomeDelivery: boolean
    merchantHasLocalPickup: boolean
    merchantLatitudeCoordinate: number | null
    merchantLongitudeCoordinate: number | null
    useStoreAddressForWelcomePack: boolean
  } | null
  propietaryContact: {
    contacFirstName: string | null
    contactSurname1: string | null
    contactSurname2: string | null
    contacDocumentType: documentType | null
    contacDocument: string | null
    contactEmailAddress: string | null
    contactPhone: string | null
    isEditable: boolean | null
    isDocumentEditable: boolean | null
  } | null
  attendantContact: {
    contacFirstName: string | null
    contactSurname1: string | null
    contactSurname2: string | null
    contactEmailAddress: string | null
    contactPhone: string | null
  } | null
  products:
    | [
        {
          productId: number
          productServices: number[]
        },
        {
          productId: number
          productServices: number[]
        },
      ]
    | null
  tpvs: ApiTpvsData[] | null
  documents: string | null
  updateDate: string | null
  endDate: string | null
}

type ApiAutoEnrollment = {
  data: ApiAutoEnrollmentModel
  meta: MetaModel
}

const convertAutoEnrollmentDataToDomain = ({
  data,
}: ApiAutoEnrollment): AutoEnrollmentModel => {
  return {
    id: data.id,
    step: computeAutoEnrollmentCurrentStep(data.processStatus),
    processType: data.processType,
    billingGeneralData: {
      cif: data?.finantialData?.merchantCIF || '',
      businessName: data?.finantialData?.merchantBusinessName || '',
    },
    billingAddressData: {
      street: data?.finantialData?.addressStreetName || '',
      number: data?.finantialData?.addressStreetNumber || '',
      additionalInfo: data?.finantialData?.addressAdditionalData12 || '',
      floorBlockStair: data?.finantialData?.addressAdditionalData1 || '',
      postalCode: data?.finantialData?.addressZipCode || '',
      city: data?.finantialData?.addressTown || '',
      province: data?.finantialData?.addressProvince || '',
    },
    billingOwnerData: {
      documentType: data?.propietaryContact?.contacDocumentType || documentType.Default,
      documentNumber: data?.propietaryContact?.contacDocument || '',
      name: data?.propietaryContact?.contacFirstName || '',
      surname1: data?.propietaryContact?.contactSurname1 || '',
      surname2: data?.propietaryContact?.contactSurname2 || '',
      email: data?.propietaryContact?.contactEmailAddress || '',
      phone: data?.propietaryContact?.contactPhone || '',
      isEditable: data?.propietaryContact?.isEditable ?? false,
      isDocumentEditable: data?.propietaryContact?.isDocumentEditable ?? false,
    },
    paymentAccountData: {
      iban: data?.finantialData?.iban || '',
    },
    paymentContactData: {
      name: data?.billingContact?.contacFirstName || '',
      surname1: data?.billingContact?.contactSurname1 || '',
      surname2: data?.billingContact?.contactSurname2 || '',
      email: data?.billingContact?.contactEmailAddress || '',
      phone: data?.billingContact?.contactPhone || '',
    },
    storeGeneralData: {
      name: data?.storeData?.merchantName || '',
      email: data?.storeData?.addressEmail || '',
      phone: data?.storeData?.addressTelephone || '',
      url: data?.storeData?.merchantWebUrl || '',
      food: {
        id: data?.storeData?.merchantCoockTypeId || null,
        name: data?.storeData?.merchantCoockType || '',
      },
      service: {
        physical: data?.storeData?.merchantIsOnlineStore ?? true,
        delivery: data?.storeData?.merchantHasHomeDelivery ?? false,
        takeAway: data?.storeData?.merchantHasLocalPickup ?? false,
      },
    },
    storeAddressData: {
      street: data?.storeData?.addressStreetName || '',
      number: data?.storeData?.addressStreetNumber || '',
      additionalInfo: data?.storeData?.addressAdditionalData12 || '',
      floorBlockStair: data?.storeData?.addressAdditionalData1 || '',
      postalCode: data?.storeData?.addressZipCode || '',
      city: data?.storeData?.addressTown || '',
      province: data?.storeData?.addressProvince || '',
      latitude: data?.storeData?.merchantLatitudeCoordinate?.toString() || '',
      longitude: data?.storeData?.merchantLongitudeCoordinate?.toString() || '',
    },
    storeContactData: {
      name: data?.attendantContact?.contacFirstName || '',
      surname1: data?.attendantContact?.contactSurname1 || '',
      surname2: data?.attendantContact?.contactSurname2 || '',
      email: data?.attendantContact?.contactEmailAddress || '',
      phone: data?.attendantContact?.contactPhone || '',
    },
    tpvData: {
      terminalMid: data?.tpvs?.map(tpv => tpv.terminalMid) ?? [],
    },
    welcomePackData: {
      useStoreAddress: data?.storeData?.useStoreAddressForWelcomePack ?? true,
    },
    products: data?.products || [],
  }
}

type ApiSaveFinantialData = {
  billingContact: {
    contacFirstName: string
    contactSurname1: string
    contactSurname2: string | null
    contactEmailAddress: string
    contactPhone: string
  }
  finantialData: {
    merchantBusinessName: string
    merchantCIF: string
    iban: string
    addressRoadTypeName: string | null
    addressStreetName: string
    addressStreetNumber: string
    addressAdditionalData1: string | null
    addressAdditionalData12: string | null
    addressZipCode: string
    addressTown: string
    addressProvince: string
  }
  propietaryContact: {
    contacFirstName: string
    contactSurname1: string
    contactSurname2: string | null
    contacDocumentType: documentType
    contacDocument: string
    contactEmailAddress: string
    contactPhone: string
  }
}

const convertStep1FormToApi = (data: Step1Form): ApiSaveFinantialData => ({
  finantialData: {
    merchantBusinessName: data.businessName,
    merchantCIF: data.cif ?? '',
    iban: data.iban,
    addressRoadTypeName: null,
    addressStreetName: data.billingStreet,
    addressStreetNumber: data.billingNumber,
    addressAdditionalData1: data.billingFloorBlockStair ?? '',
    addressAdditionalData12: data.billingAdditionalInfo ?? '',
    addressZipCode: data.billingPostalCode,
    addressTown: data.billingCity,
    addressProvince: data.billingProvince,
  },
  propietaryContact: {
    contacFirstName: data.billingName,
    contactSurname1: data.billingSurname1,
    contactSurname2: data.billingSurname2 ?? '',
    contacDocumentType: data.documentType,
    contacDocument: data.documentNumber,
    contactEmailAddress: data.billingEmail,
    contactPhone: data.billingPhone,
  },
  billingContact: {
    contacFirstName: data.paymentName,
    contactSurname1: data.paymentSurname1,
    contactSurname2: data.paymentSurname2 ?? '',
    contactEmailAddress: data.paymentEmail,
    contactPhone: data.paymentPhone,
  },
})

type ApiTerminals = {
  terminalMid: string
}

type ApiSaveStoreData = {
  storeData: {
    merchantName: string
    addressRoadTypeName: string | null
    addressStreetName: string
    addressStreetNumber: string
    addressAdditionalData1: string | null
    addressAdditionalData12: string | null
    addressZipCode: string
    addressTown: string
    addressProvince: string
    addressTelephone: string | null
    addressEmail: string
    merchantWebUrl: string | null
    merchantCoockTypeId: number
    merchantCoockType: string
    merchantIsOnlineStore: boolean
    merchantHasHomeDelivery: boolean
    merchantHasLocalPickup: boolean
    merchantLatitudeCoordinate: number
    merchantLongitudeCoordinate: number
    useStoreAddressForWelcomePack: boolean
  }
  attendantContact: {
    contacFirstName: string
    contactSurname1: string
    contactEmailAddress: string
    contactPhone: string
  }
  tpvs: ApiTerminals[]
}

const convertStep2FormToApi = (data: Step2Form): ApiSaveStoreData => ({
  storeData: {
    merchantName: data.storeName,
    addressRoadTypeName: null,
    addressStreetName: data.storeStreet,
    addressStreetNumber: data.storeNumber,
    addressAdditionalData1: data.storeFloorBlockStair ?? '',
    addressAdditionalData12: data.storeAdditionalInfo ?? '',
    addressZipCode: data.storePostalCode,
    addressTown: data.storeCity,
    addressProvince: data.storeProvince,
    addressTelephone: data.storePhone ?? null,
    addressEmail: data.storeEmail ?? '',
    merchantWebUrl: data.storeWeb ?? '',
    merchantCoockTypeId: data.storeFoodTypeId ?? 0,
    merchantCoockType: data.storeFoodType ?? '',
    merchantIsOnlineStore: !data.storeIsOnlineStore,
    merchantHasHomeDelivery: data.storeHasHomeDelivery ?? false,
    merchantHasLocalPickup: data.storeHasLocalPickup ?? false,
    merchantLatitudeCoordinate: parseFloat(data.storeLatitude),
    merchantLongitudeCoordinate: parseFloat(data.storeLongitude),
    useStoreAddressForWelcomePack: data.useStoreAddressForWelcomePack === 'true',
  },
  attendantContact: {
    contacFirstName: data.contactName,
    contactSurname1: data.contactSurname1 + ' ' +  data.contactSurname2,
    contactEmailAddress: data.contactEmail ?? '',
    contactPhone: data.contactPhone ?? '',
  },
  tpvs:
    data.terminalMid?.map(terminal => ({
      terminalMid: terminal.value,
    })) ?? [],
})

type ApiUpdateOwnerData = {
  propietaryContact: {
    contacFirstName: string
    contactSurname1: string
    contacDocumentType: number
    contacDocument: string
    contactEmailAddress: string
    contactPhone: string
    isEditable: boolean
  }
}

const convertOwnerDataFormToApi = (data: OwnerDataForm): ApiUpdateOwnerData => ({
  propietaryContact: {
    contacFirstName: data.name,
    contactSurname1: data.surname1 ?? '',
    contacDocumentType: data.documentType ?? 0,
    contacDocument: data.documentNumber,
    contactEmailAddress: data.email,
    contactPhone: data.phone ?? '',
    isEditable: false,
  },
})

export class ApiAutoEnrollmentRepository implements AutoEnrollmentRepository {
  async GetAutoEnrollmentData(): Promise<HttpModel<AutoEnrollmentModel>> {
    const response = await axiosFetch(getAutoEnrollmentData, HttpMethod.GET)

    if (!response.data) {
      return {
        data: {} as AutoEnrollmentModel,
        meta: response.meta,
      }
    }

    return {
      data: convertAutoEnrollmentDataToDomain(response),
      meta: response.meta,
    }
  }

  async GetAutoEnrollmentProductData(): Promise<HttpModel<AutoEnrollmentModel>> {
    const response = await axiosFetch(getAutoEnrollmentProductData, HttpMethod.GET)

    if (!response.data) {
      return {
        data: {} as AutoEnrollmentModel,
        meta: response.meta,
      }
    }

    return {
      data: convertAutoEnrollmentDataToDomain(response),
      meta: response.meta,
    }
  }

  async CheckCifAlreadyExists(cif: string): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      checkCifAlreadyExists.concat(createQueryString({ merchantCIF: cif })),
      HttpMethod.GET,
    )

    if (!response.data) {
      return {
        data: false,
        meta: response.meta,
      }
    }

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetDefaultConditionsData(): Promise<
    HttpModel<AutoEnrollmentDefaultConditionsModel[]>
  > {
    const response = await axiosFetch(getAutoEnrollmentDefaultConditions, HttpMethod.GET)

    if (!response.data) {
      return {
        data: [],
        meta: response.meta,
      }
    }

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async GetDocumentsData(
    processType: AutoEnrollmentTypes,
  ): Promise<HttpModel<AutoEnrollmentDocumentsModel>> {
    const response = await axiosFetch(
      getAutoEnrollmentDocuments.concat(
        createQueryString({
          processType: processType.toString(),
        }),
      ),
      HttpMethod.GET,
    )

    if (!response.data) {
      return {
        data: {
          documentsForSigning: [],
        },
        meta: response.meta,
      }
    }

    return {
      data: {
        documentsForSigning: response.data?.documentsForSigning?.map(
          (document: AutoEnrollmentDocumentsModel, index: number) => ({
            ...document,
            signingId: index,
          }),
        ),
      },
      meta: response.meta,
    }
  }

  async GetAutoEnrollmentLogaltyStatus(
    processType: AutoEnrollmentTypes,
  ): Promise<HttpModel<LogaltyResponseStatus>> {
    const response = await axiosFetch(
      getAutoEnrollmentLogaltyStatus.concat(
        createQueryString({
          processType: processType.toString(),
        }),
      ),
      HttpMethod.GET,
      {},
    )

    if (!response.data) {
      return {
        data: LogaltyResponseStatus.Pending,
        meta: response.meta,
      }
    }

    return {
      data: computeLogaltyResponseStatus(response),
      meta: response.meta,
    }
  }

  async SaveAutoEnrollmentStep1(data: Step1Form): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      saveAutoEnrollmentFinantialData,
      HttpMethod.PUT,
      convertStep1FormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async SaveAutoEnrollmentStep2(data: Step2Form): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      saveAutoEnrollmentStoreData,
      HttpMethod.PUT,
      convertStep2FormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async SaveAutoEnrollmentStep3(data: Step3Form): Promise<HttpModel<LogaltyModel>> {
    const response = await axiosFetch(saveAutoEnrollmentProducts, HttpMethod.POST, data)

    return {
      data: response.data,
      meta: response.meta,
    }
  }

  async UpdateOwnerData(data: OwnerDataForm): Promise<HttpModel<boolean>> {
    const response = await axiosFetch(
      saveAutoEnrollmentOwnerData,
      HttpMethod.PUT,
      convertOwnerDataFormToApi(data),
    )

    return {
      data: response.data,
      meta: response.meta,
    }
  }
}
